'use client';

import { useState } from 'react';
import { Share2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { ShareButtonProps } from '../types';

export default function ShareButton({
  url,
  title,
  description = '',
  onShare
}: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const isSecureContext = typeof window !== 'undefined' && 
    (window.isSecureContext || window.location.protocol === 'https:');

  const hasWebShare = typeof navigator !== 'undefined' && 
    'share' in navigator && 
    'userActivation' in navigator;

  const canUseWebShare = isSecureContext && hasWebShare;

  const handleShare = () => {
    if (canUseWebShare && navigator.userActivation?.isActive) {
      navigator.share({
        title,
        text: description,
        url
      }).then(() => {
        onShare?.('native');
        toast.success('Shared successfully');
      }).catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        console.warn('Web Share failed:', error.name);
        handleCopyFallback();
      });
    } else {
      handleCopyFallback();
    }
  };

  const handleCopyFallback = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        onShare?.('copy');
        toast.success('Link copied to clipboard');
      }).catch(() => {
        showManualCopyMessage();
      });
    } else {
      showManualCopyMessage();
    }
  };

  const showManualCopyMessage = () => {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const message = isIOS 
      ? 'Copy URL from Safari address bar' 
      : 'Copy URL from browser address bar';
    toast.error(message);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleShare}
      className="flex items-center gap-2"
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Share2 className="h-4 w-4" />
      )}
      <span className="hidden sm:inline">
        {copied ? 'Copied!' : 'Share'}
      </span>
    </Button>
  );
} 